import { Component, inject, signal, computed, HostListener } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  Router,
  RouterOutlet,
  NavigationEnd,
  RouterLink,
  RouterLinkActive,
} from "@angular/router";
import { CommonModule } from "@angular/common";
import { filter } from "rxjs/operators";
import { AuthService } from "./services/auth.service";
import { AccessService } from "./services/access.service";
import {
  AppHeaderService,
  FormBuilderHeaderConfig,
} from "./services/app-header.service";
import { ToastService } from "./services/toast.service";
import { ThemeService } from "./services/theme.service";
import { ContextSelectorComponent } from "./components/shared/context-selector/context-selector.component";
import { LogoComponent } from "./components/shared/logo/logo.component";
import { ToastComponent } from "./components/shared/toast/toast.component";
import { environment } from "../environments/environment";
import { CallerAuthority } from "./models/role.model";

/** ROLE_LABELS reads "Business" for an admin, which says nothing on its own. */
const SIDEBAR_ROLE_LABELS: Record<CallerAuthority, string> = {
  SUPER_ADMIN: "Super Admin",
  BUSINESS: "Business Admin",
  OWNER: "Branch Owner",
  MANAGER: "Branch Manager",
};

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    ContextSelectorComponent,
    LogoComponent,
    ToastComponent,
  ],
  templateUrl: "./app.component.html",
})
export class AppComponent {
  private authService = inject(AuthService);
  private accessService = inject(AccessService);
  private router = inject(Router);
  private appHeaderService = inject(AppHeaderService);
  private themeService = inject(ThemeService);

  toast = inject(ToastService);
  headerConfig = this.appHeaderService.config;

  isDark = computed(() => this.themeService.theme() === "dark");

  toggleTheme(): void {
    this.themeService.toggle();
  }

  formBuilderHeader = computed(() => {
    const c = this.appHeaderService.config();
    return c?.type === "form-builder" ? c : null;
  });

  private readonly PAGE_TITLES: Record<string, string> = {
    "/home": "Home",
    "/checklists": "Checklists",
    "/reports": "Compliance",
    "/task-history": "Task History",
    // No /setup/* entries: the startsWith("/setup") fallback below covers every
    // section, and listing them was how /settings/employees came to be missing.
    "/forms/new": "New Form",
    "/documents": "Documents",
    "/admin/database": "Dev Database",
  };

  private currentUrl = signal(this.router.url);

  title = computed(() => {
    const url = this.currentUrl();
    if (this.PAGE_TITLES[url]) return this.PAGE_TITLES[url];
    if (url.startsWith("/checklists")) return "Checklists";
    if (url.startsWith("/reports")) return "Compliance";
    if (url.startsWith("/task-history")) return "Task History";
    if (url.match(/^\/forms\/[^/]+\/edit$/)) return "Edit Form";
    if (url.match(/^\/forms\/[^/]+\/preview$/)) return "Preview Form";
    if (url.startsWith("/setup")) return "Setup";
    if (url.startsWith("/documents")) return "Documents";
    if (url.startsWith("/scan/")) return "Scan";
    // Only reachable for the instant before the first NavigationEnd, when
    // currentUrl is still the pre-redirect "/".
    return "Taolo";
  });

  /**
   * Routes that render bare, with no sidebar, header or context selector.
   *
   * Named for what it does rather than for the auth pages, because /scan is not
   * a sign-in page and calling it one is a lie the next reader trips over. It
   * has to be here: the shell mounts ContextSelectorComponent, which drives
   * AccessService, and an anonymous scanner has no user for it to work from.
   */
  hideAppShell = computed(() => this.isScanPage() || this.isAuthPage());

  /** The auth pages: one card, centred, never taller than the viewport. */
  isAuthPage = computed(() => {
    const url = this.currentUrl();
    return (
      url === "/sign-in" ||
      url.startsWith("/set-password")
    );
  });

  /**
   * The public scan page, which hides the shell for the same reason as the auth
   * pages but needs a DIFFERENT bare layout, not theirs.
   *
   * The auth layout centres its child inside the root's `h-screen
   * overflow-hidden`, which is right for a sign-in card and wrong for anything
   * that can outgrow the viewport: a branch with a long staff list rendered
   * clipped at both ends with nothing to scroll. This page owns its own height
   * and needs a scroll container, so it gets one.
   */
  isScanPage = computed(() => this.currentUrl().startsWith("/scan/"));

  user = this.authService.user;

  sidebarOpen = signal(false);

  toggleSidebar(): void {
    this.sidebarOpen.update((v) => !v);
  }

  closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  /**
   * Setup is for people with standing in a business. Was gated on `loaded()`
   * alone, i.e. every authenticated user — who then landed on a Forms table they
   * had no permission to read.
   *
   * One predicate for the whole tab since Settings folded into Setup; the two
   * were already the same check under different names. Which *sections* a user
   * sees inside is SetupComponent's business.
   */
  canViewSetup = computed(() => this.accessService.hasAnyBusinessScope());

  /** The same line as Compliance: this is the evidence behind that score. */
  canViewTaskHistory = computed(() =>
    this.accessService.canViewTaskHistory(
      this.accessService.selectedBusinessId(),
    ),
  );

  /**
   * The Compliance tab. Any branch role in the selected business — what each
   * role then sees is narrowed by the Task API, not here.
   */
  canViewReports = computed(() =>
    this.accessService.canViewReports(this.accessService.selectedBusinessId()),
  );

  /**
   * The line under the logo: what the signed-in user is in the business they
   * are looking at. It used to read "Franchise Admin" for everyone, Managers
   * included — a label claiming authority the user does not have. Empty until
   * the memberships have loaded, rather than guessing.
   */
  roleLabel = computed(() => {
    const authority = this.accessService.businessAuthority(
      this.accessService.selectedBusinessId(),
    );
    return authority ? SIDEBAR_ROLE_LABELS[authority] : "";
  });

  /**
   * Start/stop for the dev Cloud SQL instance. The SuperAdmin claim alone —
   * this is infrastructure rather than business data, so hasAnyBusinessScope()
   * does not apply.
   *
   * Hidden in prod because that project has no Cloud SQL instance. The callable
   * refuses there independently, so this is cosmetic rather than a control.
   */
  canViewDevDatabase = computed(
    () => this.accessService.isSuperAdmin() && !environment.production,
  );

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.currentUrl.set(event.urlAfterRedirects);
          this.sidebarOpen.set(false);
        }
      });
  }

  /**
   * Clear collapsed selection (caret) after a single click in read-only content
   * so users can highlight/drag-to-select to copy but not "place a cursor".
   * Never run when focus is in an input, textarea, or contenteditable.
   */
  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    const active = document.activeElement;
    if (active?.closest('input, textarea, [contenteditable="true"]')) return;
    const sel = window.getSelection();
    if (!sel || !sel.isCollapsed) return;
    const node = sel.anchorNode;
    if (!node) return;
    const el = node.nodeType === Node.ELEMENT_NODE ? node as Element : (node as Node).parentElement;
    if (!el) return;
    const editable = el.closest('input, textarea, [contenteditable="true"]');
    if (editable) return;
    sel.removeAllRanges();
  }

  signOut(): void {
    this.authService.signOut();
  }

  onFormBuilderTitleChange(cfg: FormBuilderHeaderConfig, value: string): void {
    cfg.title.set(value);
    cfg.onTitleChange?.(value);
  }
}
